import { ApiError, type VersionConflictBody } from '../api/transport';

/**
 * Versiones contra choques (docs/versiones-contra-choques.md).
 *
 * A save that comes back 409 `version_conflict` carries the record as it is
 * saved now. The screen compares three drafts: what the form started from
 * (`base`), what this person typed (`mine`) and what is saved (`theirs`).
 *
 * - Nobody touched the same field: `merge`. The screen resends only what this
 *   person changed, with the fresh version, and both changes stay.
 * - Both changed a field to different values: `ask`. The form keeps what was
 *   typed and shows each clash until the person picks "Guardar lo mío" (send
 *   `patch` with the fresh version) or "Quedarme con lo nuevo".
 */

export type VersionConflictError = ApiError & { conflict: VersionConflictBody };

export function isVersionConflict(error: unknown): error is VersionConflictError {
  return error instanceof ApiError && error.status === 409 && error.code === 'version_conflict'
    && error.conflict !== undefined;
}

export type ConflictClash<Draft> = {
  [Key in keyof Draft & string]: { key: Key; mine: Draft[Key]; theirs: Draft[Key] };
}[keyof Draft & string];

export type ConflictPlan<Draft> =
  | { kind: 'merge'; patch: Partial<Draft> }
  | { kind: 'ask'; clashes: ConflictClash<Draft>[]; patch: Partial<Draft> };

export function planVersionConflict<Draft extends Record<string, unknown>>(input: {
  base: Draft;
  mine: Draft;
  theirs: Draft;
  keys: readonly (keyof Draft & string)[];
}): ConflictPlan<Draft> {
  const { base, mine, theirs, keys } = input;
  const mineChanged = keys.filter((key) => !sameValue(mine[key], base[key]));
  const patch = Object.fromEntries(
    mineChanged.filter((key) => !sameValue(mine[key], theirs[key])).map((key) => [key, mine[key]]),
  ) as Partial<Draft>;
  const clashes = mineChanged
    .filter((key) => !sameValue(theirs[key], base[key]) && !sameValue(mine[key], theirs[key]))
    .map((key) => ({ key, mine: mine[key], theirs: theirs[key] }) as ConflictClash<Draft>);
  return clashes.length ? { kind: 'ask', clashes, patch } : { kind: 'merge', patch };
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
